import {
  WS_RECONNECT_BASE_MS,
  WS_RECONNECT_MAX_ATTEMPTS,
  WS_RECONNECT_MAX_DELAY_MS,
} from './constants';
import { markNetworkRecovered } from './networkDrop';
import { releasePublisherPeerConnection } from './publisher';
import type { JanusCallRefs } from './sessionRefs';
import { callT } from '~/utils/callI18n';
import { logJanus, waitForNetwork } from './utils';

export function scheduleWebSocketReconnect(
  r: JanusCallRefs,
  isActiveCallSession: () => boolean,
  immediate = false
): void {
  if (r.signalingResyncInFlightRef.current) return;
  if (r.intentionalWsCloseRef.current && !r.networkDropPendingRef.current) return;
  if (!isActiveCallSession()) return;

  const attempt = r.wsReconnectAttemptsRef.current;
  if (attempt >= WS_RECONNECT_MAX_ATTEMPTS) {
    logJanus('RECONNECT', 'max WS attempts — notify app, keep call UI');
    r.wsReconnectAttemptsRef.current = 0;
    r.onMediaConnectionLostRef.current?.();
    return;
  }

  if (r.wsReconnectTimerRef.current) clearTimeout(r.wsReconnectTimerRef.current);
  const delay = immediate ? 0 : Math.min(WS_RECONNECT_MAX_DELAY_MS, WS_RECONNECT_BASE_MS * 2 ** attempt);

  r.wsReconnectTimerRef.current = setTimeout(() => {
    r.wsReconnectTimerRef.current = null;
    r.wsReconnectAttemptsRef.current += 1;
    void r.reconnectWebSocketAndIceRef.current();
  }, delay);
}

export interface ReconnectDeps {
  cleanupSubscribers: () => void;
  rebuildPublisherPeerConnection: () => boolean;
  refreshLocalStreamForReconnect: () => Promise<boolean>;
  ensureSession: () => Promise<void>;
  sendPublisherJoin: (transaction: string, withSdpOffer: boolean) => Promise<void>;
  sendPublisherMessage: (
    body: Record<string, unknown>,
    jsep?: RTCSessionDescriptionInit,
    transaction?: string
  ) => void;
  cancelWebSocketReconnect: () => void;
  scheduleWebSocketReconnect: () => void;
  setRemoteStream: (stream: MediaStream | null) => void;
}

function localAudioNeedsRefresh(r: JanusCallRefs): boolean {
  if (r.pendingLocalStreamRefreshRef.current) return true;
  const tracks = r.localStreamRef.current?.getAudioTracks() ?? [];
  return tracks.length === 0 || tracks.every((t) => t.readyState !== 'live');
}

export async function reconnectWebSocketAndIce(
  r: JanusCallRefs,
  deps: ReconnectDeps
): Promise<void> {
  if (r.signalingResyncInFlightRef.current || !r.roomIdRef.current) {
    return;
  }
  r.signalingResyncInFlightRef.current = true;

  try {
    r.reconnectCycleRef.current += 1;
    logJanus('RECONNECT', 'start full reconnect', {
      cycle: r.reconnectCycleRef.current,
      room: r.roomIdRef.current,
      pendingStreamRefresh: r.pendingLocalStreamRefreshRef.current,
    });
    r.wsReconnectAttemptsRef.current = 0;

    r.intentionalWsCloseRef.current = true;
    if (r.keepAliveIntervalRef.current) {
      clearInterval(r.keepAliveIntervalRef.current);
      r.keepAliveIntervalRef.current = null;
    }

    if (r.sessionIdRef.current) {
      r.ghostSessionIdRef.current = r.sessionIdRef.current;
    }

    if (r.wsRef.current) {
      r.wsRef.current.onclose = null;
      r.wsRef.current.onerror = null;
      r.wsRef.current.close();
      r.wsRef.current = null;
    }

    deps.cleanupSubscribers();
    deps.setRemoteStream(null);

    releasePublisherPeerConnection(r);

    if (localAudioNeedsRefresh(r)) {
      const refreshed = await deps.refreshLocalStreamForReconnect();
      if (!refreshed) {
        throw new Error(callT('errors.reconnectRefreshLocalFailed', r.translateCallRef.current));
      }
      r.pendingLocalStreamRefreshRef.current = false;
      r.micDisconnectHandledRef.current = false;
    }

    if (!deps.rebuildPublisherPeerConnection()) {
      throw new Error(callT('errors.reconnectNoLocalAudio', r.translateCallRef.current));
    }

    r.sessionIdRef.current = null;
    r.handleIdRef.current = null;
    r.isSessionReadyRef.current = false;
    r.isPublisherJoinedRef.current = false;
    r.mediaReconnectPendingRef.current = true;

    await waitForNetwork();

    r.pendingAttachAfterCreateRef.current = 'attach-reconnect';
    await deps.ensureSession();
    r.intentionalWsCloseRef.current = false;

    await deps.sendPublisherJoin('join-pub-resync', true);

    deps.cancelWebSocketReconnect();
    r.wsReconnectAttemptsRef.current = 0;
    markNetworkRecovered(r);
    logJanus('RECONNECT', 'done — waiting joined + subscriber offer', { cycle: r.reconnectCycleRef.current });
    setTimeout(() => {
      deps.sendPublisherMessage({ request: 'listparticipants' }, undefined, 'list-participants-post-reconnect');
    }, 800);
  } catch (err) {
    logJanus('RECONNECT', 'failed', err);
    deps.scheduleWebSocketReconnect();
  } finally {
    r.signalingResyncInFlightRef.current = false;
  }
}
